import React, { useState, useEffect, useCallback } from "react";
import {
    Container,
    Typography,
    Box,
    Card,
    CardContent,
    Grid,
    Button,
    TextField,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Chip,
    IconButton,
    Stack,
    CircularProgress,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    FormControl,
    InputLabel,
    Select,
    MenuItem
} from "@mui/material";
import {
    Add as AddIcon,
    Edit as EditIcon,
    Delete as DeleteIcon,
    Search as SearchIcon,
    Refresh as RefreshIcon
} from "@mui/icons-material";
import { useAuth } from "../../../context/AuthContext";
import { usePermissions } from "../../../utils/permissions";

/**
 * Página de gestión de usuarios para administradores de negocio
 */
const UserManagement = () => {
    const { user } = useAuth();
    const permissions = usePermissions(user);
    const [loading, setLoading] = useState(true);
    const [users, setUsers] = useState([]);
    const [filteredUsers, setFilteredUsers] = useState([]);
    const [searchTerm, setSearchTerm] = useState("");
    const [statusFilter, setStatusFilter] = useState("all");
    const [roleFilter, setRoleFilter] = useState("all");
    const [selectedUser, setSelectedUser] = useState(null);
    const [editDialogOpen, setEditDialogOpen] = useState(false);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

    useEffect(() => {
        loadUsers();
    }, []);

    const filterUsers = useCallback(() => {
        let filtered = users;

        // Filtrar por término de búsqueda
        if (searchTerm) {
            filtered = filtered.filter(user =>
                user.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
                user.displayName.toLowerCase().includes(searchTerm.toLowerCase())
            );
        }

        // Filtrar por estado
        if (statusFilter !== "all") {
            filtered = filtered.filter(user => user.status === statusFilter);
        }

        // Filtrar por rol
        if (roleFilter !== "all") {
            filtered = filtered.filter(user => user.role === roleFilter);
        }

        setFilteredUsers(filtered);
    }, [users, searchTerm, statusFilter, roleFilter]);

    useEffect(() => {
        filterUsers();
    }, [filterUsers]);

    const loadUsers = async () => {
        try {
            setLoading(true);
            // TODO: Implementar carga de usuarios reales
            // Simular datos para demostración
            setTimeout(() => {
                const mockUsers = [
                    {
                        id: "1",
                        email: "usuario1@example.com",
                        displayName: "Juan Pérez",
                        role: "user",
                        status: "active",
                        kycStatus: "verified",
                        createdAt: "2024-01-15T10:30:00Z",
                        lastLogin: "2024-01-20T14:22:00Z"
                    },
                    {
                        id: "2",
                        email: "usuario2@example.com",
                        displayName: "María García",
                        role: "agent",
                        status: "active",
                        kycStatus: "verified",
                        createdAt: "2024-01-10T09:15:00Z",
                        lastLogin: "2024-01-20T16:45:00Z"
                    },
                    {
                        id: "3",
                        email: "usuario3@example.com",
                        displayName: "Carlos López",
                        role: "user",
                        status: "pending",
                        kycStatus: "pending",
                        createdAt: "2024-01-18T11:20:00Z",
                        lastLogin: null
                    },
                    {
                        id: "4",
                        email: "usuario4@example.com",
                        displayName: "Ana Martínez",
                        role: "reviewer",
                        status: "active",
                        kycStatus: "verified",
                        createdAt: "2024-01-05T08:45:00Z",
                        lastLogin: "2024-01-20T12:30:00Z"
                    },
                    {
                        id: "5",
                        email: "usuario5@example.com",
                        displayName: "Luis Rodríguez",
                        role: "user",
                        status: "suspended",
                        kycStatus: "rejected",
                        createdAt: "2024-01-12T13:10:00Z",
                        lastLogin: "2024-01-15T10:20:00Z"
                    }
                ];
                setUsers(mockUsers);
                setLoading(false);
            }, 1000);
        } catch (error) {
            // console.error("Error loading users:", error);
            setLoading(false);
        }
    };



    const handleEditUser = (user) => {
        setSelectedUser(user);
        setEditDialogOpen(true);
    };

    const handleDeleteUser = (user) => {
        setSelectedUser(user);
        setDeleteDialogOpen(true);
    };

    const handleSaveUser = async (updatedUser) => {
        try {
            // TODO: Implementar actualización real
            setUsers(prev => prev.map(user => 
                user.id === updatedUser.id ? updatedUser : user
            ));
            setEditDialogOpen(false);
            setSelectedUser(null);
        } catch (error) {
            // console.error("Error updating user:", error);
        }
    };

    const handleDeleteConfirm = async () => {
        try {
            // TODO: Implementar eliminación real
            setUsers(prev => prev.filter(user => user.id !== selectedUser.id));
            setDeleteDialogOpen(false);
            setSelectedUser(null);
        } catch (error) {
            // console.error("Error deleting user:", error);
        }
    };

    const getStatusColor = (status) => {
        switch (status) {
        case "active": return "success";
        case "pending": return "warning";
        case "suspended": return "error";
        default: return "default";
        }
    };

    const getKYCStatusColor = (kycStatus) => {
        switch (kycStatus) {
        case "verified": return "success";
        case "pending": return "warning";
        case "rejected": return "error";
        default: return "default";
        }
    };

    const getRoleColor = (role) => {
        switch (role) {
        case "admin": return "error";
        case "agent": return "primary";
        case "reviewer": return "secondary";
        case "user": return "default";
        default: return "default";
        }
    };

    if (loading) {
        return (
            <Container maxWidth="lg" sx={{ py: 4 }}>
                <Box display="flex" justifyContent="center" alignItems="center" minHeight="50vh">
                    <CircularProgress size={60} />
                </Box>
            </Container>
        );
    }

    return (
        <Container maxWidth="lg" sx={{ py: 4 }}>
            <Stack spacing={4}>
                {/* Header */}
                <Box>
                    <Typography variant="h3" component="h1" gutterBottom sx={{ fontWeight: "bold" }}>
                        Gestión de Usuarios
                    </Typography>
                    <Typography variant="h6" color="text.secondary">
                        Administra usuarios del sistema y sus permisos
                    </Typography>
                </Box>

                {/* Estadísticas rápidas */}
                <Grid container spacing={3}>
                    <Grid item xs={12} sm={6} md={3}>
                        <Card>
                            <CardContent>
                                <Typography variant="h4" color="primary" sx={{ fontWeight: "bold" }}>
                                    {users.length}
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    Total de Usuarios
                                </Typography>
                            </CardContent>
                        </Card>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                        <Card>
                            <CardContent>
                                <Typography variant="h4" color="success.main" sx={{ fontWeight: "bold" }}>
                                    {users.filter(u => u.status === "active").length}
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    Usuarios Activos
                                </Typography>
                            </CardContent>
                        </Card>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                        <Card>
                            <CardContent>
                                <Typography variant="h4" color="warning.main" sx={{ fontWeight: "bold" }}>
                                    {users.filter(u => u.kycStatus === "pending").length}
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    KYC Pendientes
                                </Typography>
                            </CardContent>
                        </Card>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                        <Card>
                            <CardContent>
                                <Typography variant="h4" color="error.main" sx={{ fontWeight: "bold" }}>
                                    {users.filter(u => u.status === "suspended").length}
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    Usuarios Suspendidos
                                </Typography>
                            </CardContent>
                        </Card>
                    </Grid>
                </Grid>

                {/* Controles de filtrado */}
                <Card>
                    <CardContent>
                        <Grid container spacing={3} alignItems="center">
                            <Grid item xs={12} md={4}>
                                <TextField
                                    fullWidth
                                    label="Buscar usuarios"
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    InputProps={{
                                        startAdornment: <SearchIcon sx={{ mr: 1, color: "text.secondary" }} />
                                    }}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6} md={3}>
                                <FormControl fullWidth>
                                    <InputLabel>Estado</InputLabel>
                                    <Select
                                        value={statusFilter}
                                        label="Estado"
                                        onChange={(e) => setStatusFilter(e.target.value)}
                                    >
                                        <MenuItem value="all">Todos</MenuItem>
                                        <MenuItem value="active">Activo</MenuItem>
                                        <MenuItem value="pending">Pendiente</MenuItem>
                                        <MenuItem value="suspended">Suspendido</MenuItem>
                                    </Select>
                                </FormControl>
                            </Grid>
                            <Grid item xs={12} sm={6} md={3}>
                                <FormControl fullWidth>
                                    <InputLabel>Rol</InputLabel>
                                    <Select
                                        value={roleFilter}
                                        label="Rol"
                                        onChange={(e) => setRoleFilter(e.target.value)}
                                    >
                                        <MenuItem value="all">Todos</MenuItem>
                                        <MenuItem value="user">Usuario</MenuItem>
                                        <MenuItem value="agent">Agente</MenuItem>
                                        <MenuItem value="reviewer">Revisor</MenuItem>
                                        <MenuItem value="admin">Administrador</MenuItem>
                                    </Select>
                                </FormControl>
                            </Grid>
                            <Grid item xs={12} md={2}>
                                <Stack direction="row" spacing={1}>
                                    <Button
                                        variant="outlined"
                                        startIcon={<RefreshIcon />}
                                        onClick={loadUsers}
                                    >
                                        Actualizar
                                    </Button>
                                    {permissions.hasPermission("business:manage_users") && (
                                        <Button
                                            variant="contained"
                                            startIcon={<AddIcon />}
                                            onClick={() => setEditDialogOpen(true)}
                                        >
                                            Nuevo
                                        </Button>
                                    )}
                                </Stack>
                            </Grid>
                        </Grid>
                    </CardContent>
                </Card>

                {/* Tabla de usuarios */}
                <Card>
                    <CardContent>
                        <TableContainer component={Paper} variant="outlined">
                            <Table>
                                <TableHead>
                                    <TableRow>
                                        <TableCell>Usuario</TableCell>
                                        <TableCell>Rol</TableCell>
                                        <TableCell>Estado</TableCell>
                                        <TableCell>KYC</TableCell>
                                        <TableCell>Último Acceso</TableCell>
                                        <TableCell>Acciones</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {filteredUsers.map((user) => (
                                        <TableRow key={user.id}>
                                            <TableCell>
                                                <Box>
                                                    <Typography variant="body1" sx={{ fontWeight: "medium" }}>
                                                        {user.displayName}
                                                    </Typography>
                                                    <Typography variant="body2" color="text.secondary">
                                                        {user.email}
                                                    </Typography>
                                                </Box>
                                            </TableCell>
                                            <TableCell>
                                                <Chip
                                                    label={user.role}
                                                    color={getRoleColor(user.role)}
                                                    size="small"
                                                />
                                            </TableCell>
                                            <TableCell>
                                                <Chip
                                                    label={user.status}
                                                    color={getStatusColor(user.status)}
                                                    size="small"
                                                />
                                            </TableCell>
                                            <TableCell>
                                                <Chip
                                                    label={user.kycStatus}
                                                    color={getKYCStatusColor(user.kycStatus)}
                                                    size="small"
                                                />
                                            </TableCell>
                                            <TableCell>
                                                <Typography variant="body2">
                                                    {user.lastLogin 
                                                        ? new Date(user.lastLogin).toLocaleDateString()
                                                        : "Nunca"
                                                    }
                                                </Typography>
                                            </TableCell>
                                            <TableCell>
                                                <Stack direction="row" spacing={1}>
                                                    {permissions.hasPermission("business:manage_users") && (
                                                        <>
                                                            <IconButton
                                                                size="small"
                                                                onClick={() => handleEditUser(user)}
                                                            >
                                                                <EditIcon />
                                                            </IconButton>
                                                            <IconButton
                                                                size="small"
                                                                color="error"
                                                                onClick={() => handleDeleteUser(user)}
                                                            >
                                                                <DeleteIcon />
                                                            </IconButton>
                                                        </>
                                                    )}
                                                </Stack>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </CardContent>
                </Card>

                {/* Diálogo de edición */}
                <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} maxWidth="sm" fullWidth>
                    <DialogTitle>
                        {selectedUser ? "Editar Usuario" : "Nuevo Usuario"}
                    </DialogTitle>
                    <DialogContent>
                        <Stack spacing={3} sx={{ mt: 2 }}>
                            <TextField
                                fullWidth
                                label="Nombre"
                                defaultValue={selectedUser?.displayName || ""}
                            />
                            <TextField
                                fullWidth
                                label="Email"
                                defaultValue={selectedUser?.email || ""}
                            />
                            <FormControl fullWidth>
                                <InputLabel>Rol</InputLabel>
                                <Select defaultValue={selectedUser?.role || "user"} label="Rol">
                                    <MenuItem value="user">Usuario</MenuItem>
                                    <MenuItem value="agent">Agente</MenuItem>
                                    <MenuItem value="reviewer">Revisor</MenuItem>
                                    <MenuItem value="admin">Administrador</MenuItem>
                                </Select>
                            </FormControl>
                            <FormControl fullWidth>
                                <InputLabel>Estado</InputLabel>
                                <Select defaultValue={selectedUser?.status || "active"} label="Estado">
                                    <MenuItem value="active">Activo</MenuItem>
                                    <MenuItem value="pending">Pendiente</MenuItem>
                                    <MenuItem value="suspended">Suspendido</MenuItem>
                                </Select>
                            </FormControl>
                        </Stack>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => setEditDialogOpen(false)}>Cancelar</Button>
                        <Button variant="contained" onClick={() => handleSaveUser(selectedUser)}>
                            Guardar
                        </Button>
                    </DialogActions>
                </Dialog>

                {/* Diálogo de confirmación de eliminación */}
                <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
                    <DialogTitle>Confirmar Eliminación</DialogTitle>
                    <DialogContent>
                        <Typography>
                            ¿Estás seguro de que quieres eliminar al usuario &quot;{selectedUser?.displayName}&quot;?
                            Esta acción no se puede deshacer.
                        </Typography>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => setDeleteDialogOpen(false)}>Cancelar</Button>
                        <Button variant="contained" color="error" onClick={handleDeleteConfirm}>
                            Eliminar
                        </Button>
                    </DialogActions>
                </Dialog>
            </Stack>
        </Container>
    );
};

export default UserManagement; 